import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import EventEmitter from 'helpers/event-emitter';
import { v1 as uuid } from 'uuid';
import { tokens } from '@companycam/slab';
import { Button } from '@companycam/slab-web';

const getIconName = {
  primary: 'information-outline',
  success: 'check',
  danger: 'alert-circle-outline',
  warning: 'alert-outline',
};

const getColors = {
  info: {
    iconContainerBackground: 'var(--cc_color_utility_info)',
    iconColor: 'var(--cc_color_text_default)',
  },
  primary: {
    iconContainerBackground: 'var(--cc_color_utility_info)',
    iconColor: 'var(--cc_color_text_default)',
  },
  warning: {
    iconContainerBackground: 'var(--cc_color_utility_caution)',
    iconColor: tokens.color_text_default_le,
  },
  success: {
    iconContainerBackground: 'var(--cc_color_utility_success)',
    iconColor: 'var(--cc_color_text_default)',
  },
  danger: {
    iconContainerBackground: 'var(--cc_color_utility_destroy)',
    iconColor: 'var(--cc_color_text_default)',
  },
};

// #region styled-components
const Container = styled.div`
  z-index: 1001;
  position: fixed;
  right: var(--cc_size_spacing_m);
  top: var(--cc_size_spacing_m);
  perspective: 400px;
`;

const StyledToast = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: var(--cc_size_spacing_xs);
  width: 300px;
  padding: var(--cc_size_spacing_m);
  background: var(--cc_color_background_3);
  border-radius: var(--cc_size_border_radius_l);
  color: var(--cc_color_text_default);
  cursor: pointer;
  transition: all 0.25s ease-out;

  /* starting state for enter */
  &.toast-enter {
    opacity: 0;
    transform: translate3d(40px, 0, 0);
  }

  /* ending state for enter */
  &.toast-enter.toast-enter-active {
    opacity: 1;
    transform: translate3d(0, 0, 0);
    z-index: 1;
  }

  /* starting state for exit */
  &.toast-exit {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  /* ending state for exit */
  &.toast-exit.toast-exit-active {
    opacity: 0;
    transform: rotate3d(1, 0, 0, 60deg) scaleX(0.75);
  }
`;

const CloseIcon = styled.i`
  top: 0;
  margin-left: var(--cc_size_spacing_s);
  font-size: 18px;
  cursor: pointer;
  color: var(--cc_color_text_nonessential);
  transition: 0.15s ease;

  &:hover {
    transform: scale(1.2);
  }
`;

const Title = styled.div`
  font-size: var(--cc_size_text_s);
  font-weight: 600;
`;

const Message = styled.div`
  margin: var(--cc_size_spacing_xs) 0 0;
  white-space: pre-wrap;
  font-size: var(--cc_size_text_xs);
  line-height: 1.4;
  font-weight: 400;
`;

const MessageContainer = styled.div`
  padding-right: var(--cc_size_spacing_m);
  margin-right: auto;
`;

const ActionContainer = styled.div`
  margin-top: var(--cc_size_spacing_s);
`;

const Icon = styled.div`
  display: grid;
  place-items: center;
  flex: none;
  width: 32px;
  height: 32px;
  margin-right: var(--cc_size_spacing_m);
  background: var(--cc_color_utility_success);
  border-radius: var(--cc_size_border_radius_l);
  background-color: ${(props) => getColors[props.type].iconContainerBackground};
`;

const Glyph = styled.i`
  font-size: 20px;
  color: ${(props) => getColors[props.type].iconColor};
`;

// #endregion

const Toast = () => {
  const [toasts, setToasts] = useState([]);

  const removeToast = (id) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id),
    );
  };

  useEffect(() => {
    const addToast = ({
      type = 'success',
      title,
      message,
      duration = 3,
      actionMessage,
      onActionPress = () => {},
    }) => {
      const id = `toast-${uuid()}-${title}`;
      setToasts((currentToasts) => [
        ...currentToasts,
        { id, type, title, message, actionMessage, onActionPress },
      ]);

      if (duration) {
        setTimeout(() => removeToast(id), duration * 1000);
      }
    };

    EventEmitter.addListener('toast', addToast);

    return () => {
      EventEmitter.removeListener('toast', addToast);
    };
  }, []);

  return (
    <Container>
      <TransitionGroup>
        {toasts.map((toast, index) => {
          // if title and message are the same as previous toast, remove previous toast now
          const titleAndMessageAlreadyShowing =
            toasts[index - 1] &&
            toasts[index - 1].title === toast.title &&
            toasts[index - 1].message === toast.message;
          if (titleAndMessageAlreadyShowing) {
            removeToast(toasts[index - 1].id);
          }

          return (
            <CSSTransition key={toast.id} classNames="toast" timeout={200}>
              <StyledToast
                key={toast.id}
                type={toast.type}
                onClick={() => removeToast(toast.id)}
                className="dark-environment"
                data-testid={`toast__${toast.type}-${index}`}
              >
                <Icon type={toast.type}>
                  <Glyph
                    type={toast.type}
                    className={`mdi mdi-${getIconName[toast.type]}`}
                  />
                </Icon>
                <MessageContainer>
                  {toast.title ? <Title>{toast.title}</Title> : null}
                  {toast.message ? <Message>{toast.message}</Message> : null}
                  {toast.actionMessage ? (
                    <ActionContainer>
                      <Button
                        size="small"
                        design="outline"
                        color="secondary"
                        onClick={toast.onActionPress}
                      >
                        {/*  Font size style Needed to override a Slab button font size bug */}
                        <span style={{ fontSize: '13px' }}>
                          {toast.actionMessage}
                        </span>
                      </Button>
                    </ActionContainer>
                  ) : null}
                </MessageContainer>
                <CloseIcon
                  data-testid="toast__close-button"
                  className="mdi mdi-close"
                />
              </StyledToast>
            </CSSTransition>
          );
        })}
      </TransitionGroup>
    </Container>
  );
};

export default Toast;
